import { forwardRef, useCallback, useEffect } from "react";

import {
  clearCollapsedFlag,
  getCollapseType,
  isCollapsed,
} from "@/components/tiptap/collapse/utils";
import { handleLineNumberClick } from "@/components/tiptap/LineNumbers";
import { LockIndicatorStatic } from "@/components/tiptap/lock/LockIndicatorButton";
import { isLocked } from "@/components/tiptap/lock/utils";
import { twMerge } from "@/utils/twMerge";

import { getLineMenu } from "../LineWrapperView";

import { CollapsedLine } from "./components/CollapsedLine";
import { CollapseSection, CollapseToggle } from "./components/CollapseToggle";
import { setLineWrapperGetPos } from "./lineWrapperGetPos";

import type { DocumentModel } from "@/models/DocumentModel";
import type { Editor } from "@tiptap/core";
import type { ElementType, MouseEventHandler, PropsWithChildren } from "react";

const RENDER_NODE_AS_CHILD_IN = [
  "listItem",
  "taskItem",
  "blockquote",
  "callout",
  "tableCell",
  "tableHeader",
];

type Props = {
  className?: string;
  contentClassName?: string;
  contentComponent?: ElementType;
  doc: DocumentModel;
  renderNodeAsChildIn?: string[];
  rowClassName?: string;
  /**
   * Position of the underlying ProseMirror node for this line.
   * Used by line option buttons (e.g. duplicate).
   */
  getPos?: () => number | undefined;
};

export const LineWrapper = forwardRef<HTMLDivElement, PropsWithChildren<Props>>(
  function LineWrapper(
    {
      doc,
      children,
      className,
      contentClassName,
      rowClassName,
      renderNodeAsChildIn = RENDER_NODE_AS_CHILD_IN,
      getPos = () => undefined,
      contentComponent: ContentComponent = "div",
    },
    ref,
  ) {
    const { tiptap } = doc.editor;
    const pos = getPos();
    const parent = resolveParent(tiptap, pos);
    const node = resolveNode(tiptap, pos);
    const renderAsChild = parent && renderNodeAsChildIn.includes(parent.type.name);
    const lineMenu = getLineMenu(tiptap, node);
    const parentLineMenu = getLineMenu(tiptap, parent);
    const hasLineMenu = lineMenu !== "none" && parentLineMenu !== "none";

    const collapseType = getCollapseType(node);
    const canCollapse = !!collapseType && !renderAsChild;
    const collapsed = canCollapse && isCollapsed(node);
    const locked = isLocked(node);

    const topLevelNodeTypeName =
      parent && parent.type.name !== "document" && parent.type.name !== "documentTitleSchema"
        ? parent.type.name
        : (node?.type.name ?? "block");

    const onCollapseChange: MouseEventHandler<HTMLButtonElement> = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const nodePos = getPos();

      if (nodePos !== undefined) {
        tiptap?.chain().toggleCollapsed(nodePos, true).focus().run();
      }
    };

    const preserveFocus: MouseEventHandler<HTMLButtonElement> = (e) => e.preventDefault();

    // The block menu acts through it after awaits, so it needs the live position.
    const lineWrapperRef = useCallback(
      (element: HTMLDivElement | null) => {
        if (element) {
          setLineWrapperGetPos(element, getPos);
        }
      },
      [getPos],
    );

    const shouldClearCollapsed = renderAsChild && isCollapsed(node);
    useEffect(() => {
      if (!shouldClearCollapsed || !tiptap) {
        return;
      }

      const nodePos = getPos();
      if (nodePos !== undefined) {
        clearCollapsedFlag(tiptap, nodePos);
      }
    }, [shouldClearCollapsed, tiptap, getPos]);

    const contentComponent = (
      <ContentComponent
        ref={ref}
        data-line-content
        className={twMerge(
          "relative z-10 flex flex-1 items-center flex-nowrap",
          "sm:flex-wrap sm:w-full sm:max-w-full",
          "sm:overflow-hidden",
          contentClassName,
        )}
      >
        {children}
      </ContentComponent>
    );

    if (renderAsChild) {
      return contentComponent;
    }

    if (collapsed && collapseType === "block") {
      return (
        <CollapsedLine
          nodeTypeName={topLevelNodeTypeName}
          className={className}
          onMouseDown={preserveFocus}
          onClick={onCollapseChange}
        />
      );
    }

    return (
      <div
        ref={lineWrapperRef}
        className={twMerge(
          "line-wrapper relative max-w-content-width w-full mx-auto sm:max-w-full",
          hasLineMenu &&
            "hover:before:bg-gray-50 dark:hover:before:bg-gray-950 has-[[data-collapse-toggle]:hover]:before:bg-transparent",
          "before:content-[''] sm:before:hidden before:absolute before:left-11.5 before:top-0 before:bottom-0 before:-right-11 before:rounded-10 before:-my-1.25 before:pointer-events-none",
        )}
      >
        <div
          className={twMerge(
            "relative flex flex-1 items-center my-2.5 -mr-12 pr-12 cursor-text",
            "sm:mr-0 sm:pr-0",
            className,
          )}
        >
          <div
            className={twMerge(
              "group/line flex flex-1 items-baseline min-w-0",
              "sm:max-w-full",
              rowClassName,
            )}
          >
            <div
              // TODO: dynamic line numbers width
              // https://linear.app/corca-research/issue/D-2987/dynamic-line-numbers-column-width
              className="relative flex items-center select-none fromSm:-ml-7"
              contentEditable={false}
              // The block menu has the gutter's actions, without reading them out on every line.
              aria-hidden
            >
              {locked && <LockIndicatorStatic getPos={getPos} />}
              <button
                type="button"
                className="line-number text-xxs text-right sm:text-left w-line-number-width sm:w-6.25 text-gray-400 dark:text-gray-600 selection:bg-transparent"
                onClick={handleLineNumberClick}
                tabIndex={-1}
              />
              <div className="flex shrink-0 items-center justify-center w-7 sm:hidden">
                {canCollapse && collapseType === "block" && (
                  <CollapseToggle
                    collapsed={collapsed}
                    nodeTypeName={topLevelNodeTypeName}
                    className="opacity-0 group-hover/line:opacity-100"
                    onMouseDown={preserveFocus}
                    onClick={onCollapseChange}
                  />
                )}
                {canCollapse && collapseType === "section" && (
                  <CollapseSection
                    collapsed={collapsed}
                    nodeTypeName={topLevelNodeTypeName}
                    onMouseDown={preserveFocus}
                    onClick={onCollapseChange}
                  />
                )}
              </div>
            </div>
            {contentComponent}
          </div>
        </div>
      </div>
    );
  },
);

function resolveParent(tiptap: Editor | undefined, pos: number | undefined) {
  if (!tiptap || pos == null) {
    return undefined;
  }

  const { doc } = tiptap.state;

  if (pos < 0 || pos > doc.content.size) {
    return undefined;
  }

  return doc.resolve(pos).parent;
}

function resolveNode(tiptap: Editor | undefined, pos: number | undefined) {
  if (!tiptap || pos == null) {
    return undefined;
  }

  const { doc } = tiptap.state;

  if (pos < 0 || pos > doc.content.size) {
    return undefined;
  }

  return doc.nodeAt(pos) ?? undefined;
}
